/**
 * Метаданные контура: подписи, каталоги, курс, плотность, счётчики скрытого.
 *
 * Всё это приходит с сервера, а не лежит в бандле, по одной причине: вкладка
 * сканера живёт неделями. Заведи бэкенд новую дисциплину, новое правило или
 * новую причину смерти — старый бандл обязан назвать их словами сервера, а не
 * показать код или, хуже, придуманную подпись.
 *
 * Каждое поле необязательно. Во время выката несколько секунд отвечает
 * предыдущая версия API, а откат возвращает её насовсем: читать что-либо как
 * обязательное значило бы падать на первом кадре ровно в тот момент, когда
 * человек смотрит на ленту.
 *
 * Обратное тоже верно: не всё, что сервер присылает, мы читаем. Полосы процента
 * (`roiBands`) разбирались здесь ради надписи под кнопкой «мягкие»; кнопку убрали
 * 12.08, и разбор ушёл вместе с ней. Сервер их считает и отдаёт по-прежнему —
 * пригодятся, когда для этих чисел найдётся место, где их читают.
 */

import { FEED_KIND } from '../lib/feedContext';
import { apiUrl } from '../lib/build';
import { HAND_SOURCE } from '../lib/row';
import { sportLabel, type Fork, type ForkLeg } from '../lib/protocol';
import { sportCanon } from '../lib/sports';
import { venueCanon } from './venues';

export interface VenueInfo {
  name: string;
  cur: string;
  step: string;
  exchange: boolean;
}

export interface SourceInfo {
  code: string;
  stale: boolean;
  quotes: number;
  ageSeconds: number | null;
  /** Возраст последней новой цены. Расхождение с ageSeconds — переиздание. */
  originAgeSeconds?: number | null;
  staleAfterSeconds: number | null;
}

export interface Meta {
  proto: number;
  sources: SourceInfo[];
  transferBooks: string[];
  /** Аддитивно с W0b: старый бандл ключ игнорирует. */
  tickBroken: boolean;
  tickFailures: number | null;
  lastFailureAgeSeconds: number | null;
  sportNames: Record<string, string>;
  venues: Record<string, VenueInfo>;
  checks: Record<string, string>;
  /** Те же коды словами про незнание: «стакана нет», а не «не проверено: стакан есть». */
  checksMissing: Record<string, string>;
  deaths: Record<string, string>;
  hiddenGroups: Record<string, string>;
  /** Монотонные счётчики с запуска процесса. Разностью считается «с моего прихода». */
  hidden: Record<string, number>;
  familyNames: Record<string, string>;
  lifespanByFamily: Record<string, Record<string, number>>;
  density: { tz: string; byHour: number[] };
  fx: { usdRub: number | null; asOf: string | null; stale: boolean };
  latencyMs: number | null;
  /**
   * Хвост отклика. Медиана без него успокаивает: она остаётся ровной, когда
   * каждая двадцатая строка приходит на секунды позже, — а решают именно эти
   * строки, потому что вилка живёт секунды. Контур принимается по хвосту.
   */
  latencyP95Ms: number | null;
  /** Сколько замеров за окном. Перцентиль по трём точкам — украшение, не число. */
  latencySamples: number;
  /**
   * Куда есть нажать в текущей ленте. Три числа, а не доля: «нажал и попал на
   * матч» и «попал к конторе, матч ищи сам» — разные обещания, и сложив их, мы
   * назвали бы покрытием минуту поиска на строке, живущей секунды.
   */
  entry: { legs: number; event: number; section: number };
  /**
   * Обрезка ВЫШЕ нас: сколько строк подошло под фильтр у источника и сколько он
   * согласился прислать. `null` — обрезки нет.
   *
   * Именно `null`, а не пара нулей: «ничего не срезано» и «мы не знаем» —
   * разные утверждения, и второе мы не имеем права выдавать за первое.
   */
  cutUpstream: { got: number; matched: number } | null;
  /**
   * Приход от донора в последнем такте и почему часть не доехала. `null` —
   * зеркальных каналов нет вовсе.
   *
   * База для всей честности подвала: «убрали 40» при неизвестном приходе
   * читается и как «пришло 45», и как «пришло 4000».
   */
  intake: { got: number; kept: number; reasons: Record<string, number> } | null;
  /**
   * Своя воронка с сокета (`m.funnel`). `null` — старый сервер поля не шлёт.
   * Ноль здесь врал бы «такт ещё не был», а это отдельная авария.
   */
  funnel: Funnel | null;
}

/** Пять ступеней «найдено → в кадр» плюс разложение скрытого. */
export interface Funnel {
  found: number | null;
  passedGate: number | null;
  snapshotCapped: number | null;
  truncated: number | null;
  clonesRejected: number | null;
  hiddenByClass: Record<string, number>;
  hiddenByRule: Record<string, number>;
}

export const EMPTY_META: Meta = {
  proto: 0,
  sources: [],
  transferBooks: [],
  tickBroken: false,
  tickFailures: null,
  lastFailureAgeSeconds: null,
  sportNames: {},
  venues: {},
  checks: {},
  checksMissing: {},
  deaths: {},
  hiddenGroups: {},
  hidden: {},
  familyNames: {},
  lifespanByFamily: {},
  density: { tz: 'UTC', byHour: [] },
  fx: { usdRub: null, asOf: null, stale: true },
  latencyMs: null,
  latencyP95Ms: null,
  latencySamples: 0,
  entry: { legs: 0, event: 0, section: 0 },
  cutUpstream: null,
  intake: null,
  funnel: null,
};

const dict = (raw: unknown): Record<string, string> => {
  const out: Record<string, string> = {};
  if (raw === null || typeof raw !== 'object') return out;
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === 'string') out[key] = value;
  }
  return out;
};

const counters = (raw: unknown): Record<string, number> => {
  const out: Record<string, number> = {};
  if (raw === null || typeof raw !== 'object') return out;
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === 'number' && Number.isFinite(value)) out[key] = value;
  }
  return out;
};

const numOrNull = (raw: unknown): number | null => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw === 'string') {
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

function venues(raw: unknown): Record<string, VenueInfo> {
  const out: Record<string, VenueInfo> = {};
  if (raw === null || typeof raw !== 'object') return out;
  for (const [code, value] of Object.entries(raw as Record<string, unknown>)) {
    const item = (value ?? {}) as Record<string, unknown>;
    out[code] = {
      name: typeof item.name === 'string' ? item.name : code,
      cur: typeof item.cur === 'string' ? item.cur : 'USD',
      step: typeof item.step === 'string' ? item.step : '0.01',
      exchange: item.exchange === true,
    };
  }
  return out;
}

function sources(raw: unknown): SourceInfo[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => {
    const item = (entry ?? {}) as Record<string, unknown>;
    return {
      code: String(item.code ?? '—'),
      stale: item.stale === true,
      quotes: typeof item.quotes === 'number' ? item.quotes : 0,
      ageSeconds: numOrNull(item.ageSeconds),
      originAgeSeconds: numOrNull(item.originAgeSeconds),
      staleAfterSeconds: numOrNull(item.staleAfterSeconds),
    };
  });
}

function lifespans(raw: unknown): Record<string, Record<string, number>> {
  const out: Record<string, Record<string, number>> = {};
  if (raw === null || typeof raw !== 'object') return out;
  for (const [family, value] of Object.entries(raw as Record<string, unknown>)) {
    out[family] = counters(value);
  }
  return out;
}

/** Разбор ответа `/v1/meta`. Чего сервер не прислал — остаётся пустым. */
export function readMeta(body: unknown): Meta {
  const raw = (body ?? {}) as Record<string, unknown>;
  const density = (raw.density ?? {}) as Record<string, unknown>;
  const fx = (raw.fx ?? {}) as Record<string, unknown>;
  const latency = (raw.latency ?? {}) as Record<string, unknown>;
  const entry = (raw.entry ?? {}) as Record<string, unknown>;
  const hours = Array.isArray(density.byHour)
    ? density.byHour.filter((n): n is number => typeof n === 'number')
    : [];
  return {
    proto: typeof raw.proto === 'number' ? raw.proto : 0,
    sources: sources(raw.sources),
    transferBooks: Array.isArray(raw.transferBooks)
      ? raw.transferBooks.filter((code): code is string => typeof code === 'string') : [],
    tickBroken: raw.tickBroken === true,
    tickFailures: numOrNull(raw.tickFailures),
    lastFailureAgeSeconds: numOrNull(raw.lastFailureAgeSeconds),
    sportNames: dict(raw.sportNames),
    venues: venues(raw.venues),
    checks: dict(raw.checks),
    checksMissing: dict(raw.checksMissing),
    deaths: dict(raw.deaths),
    hiddenGroups: dict(raw.hiddenGroups),
    hidden: counters(raw.hidden),
    familyNames: dict(raw.familyNames),
    lifespanByFamily: lifespans(raw.lifespanByFamily),
    density: { tz: typeof density.tz === 'string' ? density.tz : 'UTC', byHour: hours },
    fx: {
      usdRub: numOrNull(fx.usdRub),
      asOf: typeof fx.asOf === 'string' ? fx.asOf : null,
      stale: fx.stale === true,
    },
    latencyMs: numOrNull(latency.oursP50Ms),
    latencyP95Ms: numOrNull(latency.oursP95Ms),
    latencySamples: numOrNull(latency.samples) ?? 0,
    entry: {
      legs: numOrNull(entry.legs) ?? 0,
      event: numOrNull(entry.event) ?? 0,
      section: numOrNull(entry.section) ?? 0,
    },
    cutUpstream: cut(raw.cutUpstream),
    intake: intake(raw.intake),
    funnel: readFunnel(raw.funnel),
  };
}

/**
 * Каталог площадок. Без него сито кладёт каждую контору из памяти в «прочие»:
 * один столбик кодов на всю панель, пока запрос не доехал (перезагрузка 29.08).
 *
 * Зовут с двери, параллельно с сессией: иначе первый кадр ленты рисуется
 * с пустым каталогом, а сорок значков ещё и забивают очередь к той же ручке.
 */
const META_WAIT_MS = 4_000;
let inflight: Promise<Meta | null> | null = null;

export async function loadMeta(): Promise<Meta | null> {
  if (inflight !== null) return inflight;
  inflight = pullMeta().finally(() => {
    inflight = null;
  });
  return inflight;
}

async function pullMeta(): Promise<Meta | null> {
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), META_WAIT_MS);
  try {
    const res = await fetch(apiUrl('/v1/meta'), { credentials: 'same-origin', signal: ac.signal });
    if (!res.ok) return null;
    return readMeta(await res.json());
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** Кассы publication v5. Адрес не через `apiUrl`: /app ходит в /v2, пятёрка — в /v5. */
export async function loadLenta5Meta(): Promise<Meta | null> {
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), META_WAIT_MS);
  try {
    const res = await fetch('/v5/v1/meta', { credentials: 'same-origin', signal: ac.signal });
    if (!res.ok) return null;
    return readMeta(await res.json());
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** Источники и подписи пятёрки в тот же каталог сита. Код уже из основной — не дублируем. */
export function mergeMetaBooks(base: Meta, extra: Meta | null): Meta {
  if (extra === null) return base;
  const venues = { ...base.venues, ...extra.venues };
  const have = new Set(base.sources.map((one) => venueCanon(one.code)).filter(Boolean));
  const sources = [...base.sources];
  for (const one of extra.sources) {
    const code = venueCanon(one.code);
    if (code === '' || have.has(code)) continue;
    have.add(code);
    sources.push({ ...one, code });
  }
  return { ...base, venues, sources };
}

export function readFunnel(raw: unknown): Funnel | null {
  if (raw === null || typeof raw !== 'object') return null;
  const one = raw as Record<string, unknown>;
  return {
    found: numOrNull(one.found),
    passedGate: numOrNull(one.passedGate),
    snapshotCapped: numOrNull(one.snapshotCapped),
    truncated: numOrNull(one.truncated),
    clonesRejected: numOrNull(one.clonesRejected),
    hiddenByClass: counters(one.hiddenByClass),
    hiddenByRule: counters(one.hiddenByRule),
  };
}

/** Дельта шлёт потолок кадра как null: байтовый cap считает только снапшот. */
export function keepFunnel(prev: Funnel | null, incoming: Funnel): Funnel {
  if (prev === null) return incoming;
  return {
    found: incoming.found ?? prev.found,
    passedGate: incoming.passedGate ?? prev.passedGate,
    snapshotCapped: incoming.snapshotCapped ?? prev.snapshotCapped,
    truncated: incoming.truncated ?? prev.truncated,
    clonesRejected: incoming.clonesRejected ?? prev.clonesRejected,
    hiddenByClass: Object.keys(incoming.hiddenByClass).length
      ? incoming.hiddenByClass
      : prev.hiddenByClass,
    hiddenByRule: Object.keys(incoming.hiddenByRule).length
      ? incoming.hiddenByRule
      : prev.hiddenByRule,
  };
}

/** Приход донора. Отсутствие поля — `null`, а не пара нулей: см. `Meta.intake`. */
function intake(raw: unknown): Meta['intake'] {
  if (raw === null || typeof raw !== 'object') return null;
  const one = raw as Record<string, unknown>;
  const got = numOrNull(one.got);
  const kept = numOrNull(one.kept);
  if (got === null || kept === null) return null;
  return { got, kept, reasons: counters(one.reasons) };
}

/** Пара «прислано / подошло». Неполный или бессмысленный объект — это `null`. */
function cut(raw: unknown): { got: number; matched: number } | null {
  if (raw === null || typeof raw !== 'object') return null;
  const one = raw as Record<string, unknown>;
  const got = numOrNull(one.got);
  const matched = numOrNull(one.matched);
  if (got === null || matched === null || matched <= got) return null;
  return { got, matched };
}

/**
 * Как назвать площадку человеку. Код — последнее средство: «predictfun» на
 * экране означает, что подпись потерялась по дороге, и это видно.
 */
export const venueName = (meta: Meta, code: string): string => {
  if (code === HAND_SOURCE) return 'руками';
  const canon = venueCanon(code);
  if (canon === 'onexbetru') return '1xBet';
  if (canon === 'onexbet') return '1xБит';
  if (canon === 'buff') return 'Buffbet';
  if (canon === 'orbitex') return 'Orbitex';
  if ((FEED_KIND === 'main' || FEED_KIND === 'tests') && canon === '1win') return '1win';
  const named = (meta.venues[canon]?.name ?? meta.venues[code]?.name ?? '').trim();
  const fallback = (canon || code).trim();
  return named || fallback;
};

export const isExchange = (meta: Meta, code: string): boolean =>
  meta.venues[code]?.exchange === true;

/**
 * Какого рода счёт нужен, чтобы поставить здесь.
 *
 * Единственный признак, по которому список контор стоит делить. Алфавит делит по
 * первой букве названия, то есть ни по чему: «Зенит», «Леон» и «Олимпбет» стоят
 * вперемешку с BC.Game и Polymarket, а человек ищет в этом списке не букву, а
 * своё — «где у меня заведены деньги». Счетов у него обычно три-четыре, и все
 * одного рода: рублёвые открывают вместе, кошелёк заводят один раз.
 *
 * Считается из того, что сервер и так отдаёт (`cur`, `exchange`), а не из
 * таблицы брендов в вёрстке: новая контора должна попадать в свою группу от
 * выката движка, а не от выката страницы.
 *
 * `other` — площадка, которой в каталоге сервера нет вовсе. Такие приносит
 * зеркальный контур, и записать их в «зарубежные» было бы догадкой: про них мы
 * не знаем ни валюты, ни устройства.
 */
export type VenueKind = 'rub' | 'out' | 'exchange' | 'other';

const OUT_MIRROR = new Set([
  'welstroy',
  'dafabet',
  'egb',
  'freewin',
  'dexsport',
]);
const EXCHANGE_MIRROR = new Set(['bxyz', 'sharpxch']);
const RUB_MIRROR = new Set([
  'fonbet',
  'leon',
  'winline',
  'zenit',
  'olimp',
  'betboom',
  'bingoboom',
  'betcity',
  'ligastavok',
  'marathon',
  'pinup',
  'pari',
  'mostbet',
  'onexbetru',
]);

export const venueKind = (meta: Meta, code: string): VenueKind => {
  const canon = venueCanon(code);
  const one = meta.venues[canon] ?? meta.venues[code];
  if (one === undefined) {
    // Зеркальные кассы без строки в /v1/meta иначе падают в «прочие».
    if (RUB_MIRROR.has(canon)) return 'rub';
    if (OUT_MIRROR.has(canon)) return 'out';
    if (EXCHANGE_MIRROR.has(canon)) return 'exchange';
    return 'other';
  }
  if (one.exchange) return 'exchange';
  return one.cur === 'RUB' ? 'rub' : 'out';
};

const cashed = new WeakMap<Fork, { meta: Meta; out: Fork }>();

/**
 * Нога без `cur` / `step` (зеркала так приходят) получает кассу из каталога.
 * Поле суммы читает валюту по каталогу, счёт — по ноге: без этого набитые
 * 30 $ на egb считались как 30 ₽, и вторая нога выходила 0.19 $.
 */
export function withVenueCash(fork: Fork, meta: Meta): Fork {
  const legs = fork.legs;
  const bare = (leg: ForkLeg): boolean =>
    leg.unit !== 'share' && (leg.cur === undefined || leg.cur === null || leg.step === undefined || leg.step === null);
  if (legs === undefined || !legs.some(bare)) return fork;
  const hit = cashed.get(fork);
  if (hit !== undefined && hit.meta === meta) return hit.out;
  let changed = false;
  const next = legs.map((leg) => {
    if (!bare(leg)) return leg;
    const kind = venueKind(meta, leg.s);
    if (kind !== 'rub' && kind !== 'out') return leg;
    const cur = kind === 'rub' ? 'RUB' : 'USD';
    const one = meta.venues[venueCanon(leg.s)] ?? meta.venues[leg.s];
    const step = leg.step ?? (one !== undefined && one.step !== '' ? one.step : undefined);
    const out: ForkLeg = { ...leg, cur: leg.cur ?? cur, ...(step !== undefined ? { step } : {}) };
    if (out.cur === leg.cur && out.step === leg.step) return leg;
    changed = true;
    return out;
  });
  const out = changed ? { ...fork, legs: next } : fork;
  cashed.set(fork, { meta, out });
  return out;
}

export function withVenueCashAll(forks: Map<string, Fork>, meta: Meta): Map<string, Fork> {
  let out: Map<string, Fork> | null = null;
  for (const [id, fork] of forks) {
    const next = withVenueCash(fork, meta);
    if (next === fork) continue;
    out ??= new Map(forks);
    out.set(id, next);
  }
  return out ?? forks;
}

/**
 * Как назвать дисциплину. `other:table_tennis` — служебный код: приставка
 * говорит движку «в матчинге не участвует» и не говорит человеку ничего.
 *
 * Три источника подряд, и порядок неслучаен. Слово сервера главнее всех: он
 * заводит дисциплины чаще, чем выкатывается бандл. Дальше — словарь в бандле:
 * сервер называет не все коды, и без этой ступени на русской странице среди
 * «тенниса» и «хоккея» попадался голый `hockey`. Голый код остаётся последним
 * ответом: показать его честнее, чем выдумать перевод.
 */
export const sportName = (meta: Meta, code: string): string => {
  if (sportCanon(code) === 'rsix') return sportLabel('rsix');
  const given = meta.sportNames[code];
  if (given !== undefined && given !== '' && given !== '—') return given;
  const known = sportLabel(code);
  if (known !== code && known !== '—') return known;
  return code.startsWith('other:') ? code.slice('other:'.length).replace(/_/g, ' ') : code;
};

/**
 * Чего мы про строку не знаем — словами.
 *
 * Отдельный словарь, а не отрицание проверенного: старый сервер его не
 * присылает, и тогда лучше склейка, чем молчание о непроверенном.
 */
export const missingName = (meta: Meta, code: string): string =>
  meta.checksMissing[code] ??
  (meta.checks[code] !== undefined ? `не проверено: ${meta.checks[code]}` : code);
